import React, { useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'

import styles from './Login.module.scss'
import { EmailStage } from '../../common/types/enums'
import {
  PRIVACY_POLICY_ROUTE,
  USER_AGREEMENT_ROUTE,
  PERSONAL_DATA_AGREEMENT_ROUTE,
} from '../../common/utils/constants'
import { useLogin } from './useLogin'
import { Button } from '../../common/components/button/Button'

export const Login: React.FC = () => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const [localEmail, setLocalEmail] = useState('')
  const [emailError, setEmailError] = useState('')

  const {
    stage,
    loading,
    message,
    registerMessage,
    isLogin,
    legalityChecked,
    email,
    code,
    timer,
    setCode,
    requestCode,
    verifyCode,
    register,
    resendCode,
    toggleLegality,
    setStage,
    isValidEmail,
  } = useLogin()

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      setCode(pasted)
      inputRefs.current[5]?.focus()
    }
  }

  const handleEmailChange = (value: string) => {
    setLocalEmail(value)
    if (value && !isValidEmail(value)) {
      setEmailError('Используйте почту в зоне .ru или .рф')
    } else {
      setEmailError('')
    }
  }

  const handleRequestCode = () => {
    if (!isValidEmail(localEmail)) {
      setEmailError('Используйте почту в зоне .ru или .рф')
      return
    }
    setEmailError('')
    requestCode(localEmail)
  }

  const handleCodeChange = (text: string, index: number) => {
    const newCode = code.split('')
    newCode[index] = text
    setCode(newCode.join(''))
    if (text && index < 5) inputRefs.current[index + 1]?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.center}>
          <p className={styles.message}>{message || 'Загрузка…'}</p>
        </div>
      )
    }

    switch (stage) {
      case EmailStage.EMAIL_INPUT:
        return (
          <>
            <h1 className={styles.title}>Вход по email</h1>
            <p className={styles.subtitle}>
              Введите email — мы отправим код подтверждения
            </p>
            <div className={styles.inputWrapper}>
              <input
                className={`${styles.input} ${emailError ? styles.inputError : ''}`}
                placeholder="example@mail.ru"
                value={localEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                type="email"
              />
              <div className={styles.errorText}>{emailError}</div>
            </div>
            <Button
              title="Получить код"
              onClick={handleRequestCode}
              disabled={!localEmail || !isValidEmail(localEmail)}
              fullWidth
            />
            <p className={styles.info}>
              После входа вы сможете откликаться на вакансии и следить за их статусом
            </p>
          </>
        )

      case EmailStage.CODE_INPUT:
        return (
          <>
            <h1 className={styles.title}>Подтверждение</h1>
            <p className={styles.subtitle}>
              {registerMessage ||
                `Код отправлен на ${email}. Если письмо не пришло, проверьте папку Спам`}
            </p>

            {isLogin && (
              <div className={styles.codeContainer} onPaste={handlePaste}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      if (el) inputRefs.current[i] = el
                    }}
                    className={styles.codeInput}
                    value={code[i] || ''}
                    onChange={(e) => handleCodeChange(e.target.value, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    type="text"
                    maxLength={1}
                    inputMode="numeric"
                  />
                ))}
              </div>
            )}

            <div className={styles.resend}>
              <span>Не пришёл код?</span>
              {timer > 0 ? (
                <span className={styles.timer}>{timer} сек</span>
              ) : (
                <button className={styles.resendButton} onClick={resendCode} type="button">
                  Отправить ещё раз
                </button>
              )}
            </div>

            {!isLogin && (
              <div className={styles.consent}>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={legalityChecked}
                    onChange={toggleLegality}
                  />
                  <span className={styles.slider} />
                </label>
                <div className={styles.links}>
                  <span>Согласие с </span>
                  <Link to={PERSONAL_DATA_AGREEMENT_ROUTE} className={styles.link}>
                    обработкой персональных данных
                  </Link>
                  <Link to={USER_AGREEMENT_ROUTE} className={styles.link}>
                    пользовательским соглашением
                  </Link>
                  <Link to={PRIVACY_POLICY_ROUTE} className={styles.link}>
                    политикой конфиденциальности
                  </Link>
                </div>
              </div>
            )}

            {!isLogin ? (
              <Button
                title="Зарегистрироваться"
                onClick={register}
                disabled={code.length !== 6 || !legalityChecked}
                fullWidth
              />
            ) : (
              <Button
                title="Войти"
                onClick={() => verifyCode(code)}
                disabled={code.length !== 6}
                fullWidth
              />
            )}

            {message && <p className={styles.message}>{message}</p>}
          </>
        )

      case EmailStage.ERROR:
        return (
          <>
            <h1 className={styles.title}>Ошибка</h1>
            <p className={styles.subtitle}>{message || 'Что-то пошло не так'}</p>
            <Button
              title="Попробовать снова"
              onClick={() => setStage(EmailStage.EMAIL_INPUT)}
              fullWidth
            />
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>Вход — ЛОГМОЛЛ</title>
        <meta name="description" content="Войдите или зарегистрируйтесь в logmall по email" />
      </Helmet>
      <div className={styles.content}>{renderContent()}</div>
    </div>
  )
}